import { WarningOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Popover } from 'antd';
import { useCallback, useState } from 'react';

import { useMessageSendErrorAction } from '@app/components/LeadCard/ui/MessageHistory/components/MessageSendErrorAction/hooks/useMessageSendErrorAction';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { FC } from 'react';

import './MessageSendErrorAction.scss';

interface MessageSendErrorActionProps {
  clientMessageId: number | null;
  errorMessage?: string | null;
  integrationId: number | null;
}

const getSendErrorPopupContainer = (triggerNode: HTMLElement): HTMLElement =>
  triggerNode.closest<HTMLElement>('.LeadCard') ?? document.body;

export const MessageSendErrorAction: FC<MessageSendErrorActionProps> = ({
  clientMessageId,
  errorMessage,
  integrationId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { canRetry, isRetrying, retryMessage } = useMessageSendErrorAction({
    clientMessageId,
    integrationId,
  });
  const normalizedErrorMessage = errorMessage?.trim() || null;

  const retryClickHandler = useCallback(async () => {
    const isRetried = await retryMessage();

    if (isRetried) {
      setIsOpen(false);
    }
  }, [retryMessage]);

  const content = (
    <div className="MessageSendErrorAction__content">
      <div className="MessageSendErrorAction__header">
        <WarningOutlined className="MessageSendErrorAction__header-icon" />
        <span className="MessageSendErrorAction__title">
          Во время отправки сообщения возникла ошибка
        </span>
      </div>
      {normalizedErrorMessage && (
        <div className="MessageSendErrorAction__text">
          {normalizedErrorMessage}
        </div>
      )}
      <Button
        block
        className="MessageSendErrorAction__retry"
        disabled={!canRetry || isRetrying}
        loading={isRetrying}
        size="small"
        type="primary"
        onClick={retryClickHandler}
      >
        Повторить
      </Button>
    </div>
  );

  return (
    <Popover
      arrow={false}
      classNames={{
        root: 'MessageSendErrorAction__popover',
      }}
      content={content}
      getPopupContainer={getSendErrorPopupContainer}
      open={isOpen}
      placement="top"
      trigger="click"
      onOpenChange={setIsOpen}
    >
      <button
        aria-label="Ошибка отправки сообщения"
        className="MessageSendErrorAction"
        type="button"
      >
        <FontAwesomeIcon icon={'fa-solid fa-circle-exclamation' as IconProp} />
      </button>
    </Popover>
  );
};
